/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
  names: {
    signInOnly: "B2C_1A_SIGNIN_ONLY",
    signUpInvitation: "B2C_1A_SIGNUP_INVITATION",
    forgotPassword: "B2C_1A_PASSWORDRESET",
    // editProfile: 'B2C_1_edit_profile_v2',
  },
  authorities: {
    signInOnly: {
      authority: import.meta.env.VITE_OIDC_AUTHORITY,
    },
    signUpInvitation: {
      authority: import.meta.env.VITE_INVITATION_AUTHORITY,
    },
    forgotPassword: {
      authority:
        "https://EltelSwedenExt.b2clogin.com/EltelSwedenExt.onmicrosoft.com/B2C_1A_PASSWORDRESET",
    } /*
        editProfile: {
            authority: 'https://fabrikamb2c.b2clogin.com/fabrikamb2c.onmicrosoft.com/b2c_1_edit_profile_v2',
        },*/,
  },
  authorityDomain: "EltelSwedenExt.b2clogin.com",
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
  auth: {
    clientId: `${import.meta.env.VITE_CLIENT_ID}`,
    redirectUri: import.meta.env.VITE_LOGIN_REDIRECT_URI,
    authority: b2cPolicies.authorities.signInOnly.authority,
    knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
    postLogoutRedirectUri: "/", // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: "sessionStorage", // This configures where your cache will be stored
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
    },
  },
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
  apiWorkOrders: {
    //endpoint: 'http://localhost:5000/api/todolist',
    scopes: {
      read: [import.meta.env.VITE_API_WORKORDERS_READ_SCOPE],
      write: [import.meta.env.VITE_API_WORKORDERS_WRITE_SCOPE],
    },
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: [
    ...protectedResources.apiWorkOrders.scopes.read,
    ...protectedResources.apiWorkOrders.scopes.write,
  ],
};

export const buildInvitationLoginRequest = ({ idTokenHint, nonce } = {}) => {
  const extraQueryParameters = {
    ...(idTokenHint ? { id_token_hint: idTokenHint } : {}),
    ...(nonce ? { nonce } : {}),
  };

  return {
    scopes: loginRequest.scopes,
    authority: b2cPolicies.authorities.signUpInvitation.authority,
    ...(Object.keys(extraQueryParameters).length > 0
      ? { extraQueryParameters }
      : {}),
  };
};
// export const loginRequest = {
//     scopes: ["User.Read"]
// };
// export const loginRequestapi = {
//     scopes: [`api://${import.meta.env.VITE_CLIENT_ID}/access_as_user`]
// };
